import { ReactElement, useEffect, useState } from "react";
import Modal from "../../../components/Atomos/Modals/Modal";
import Eye from "../../../components/Atomos/Icons/Eye";
import Button from "../../../components/staff/Button";
import type { IntegratorRunLogAssetLink } from "../../../Services/IntegratorRunLog";

const PREVIEW_CHAR_LIMIT = 100_000;

export type AssetKind = "xml" | "json";

export interface AssetPreviewTarget {
  title: string;
  kind: AssetKind;
  link: IntegratorRunLogAssetLink;
}

interface AssetCardProps {
  title: string;
  description: string;
  kind: AssetKind;
  link: IntegratorRunLogAssetLink | null;
  extra?: string;
  emphasize?: boolean;
  onPreview: () => void;
}

export default function AssetCard({
  title,
  description,
  kind,
  link,
  extra,
  emphasize = false,
  onPreview,
}: AssetCardProps): ReactElement {
  return (
    <article
      className={`flex min-h-[72px] min-w-0 items-center gap-3 bg-white px-3 py-2 md:min-h-[78px] md:px-4 ${
        emphasize
          ? "rounded-lg border-2 border-primary shadow-sm"
          : "rounded border border-gray-100 shadow-sm"
      } ${link ? "" : "opacity-70"}`}
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-Default-light md:h-12 md:w-12">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-Default">
          {kind}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <h3
          className="truncate text-sm font-medium text-gray-font font-decimal-medium"
          title={description}
        >
          {title}
        </h3>
        <p className="mt-0.5 truncate text-xs text-gray-500">
          {link ? link.fileName : "Not stored"}
          {extra ? ` · ${extra}` : ""}
        </p>
      </div>
      {link && (
        <div className="flex shrink-0 gap-1">
          <Button
            className="!w-auto !p-2"
            color="neutral"
            variant="ghost"
            size="fixed"
            tooltip="Preview"
            icon={<Eye className="h-5 w-5" />}
            onClick={onPreview}
          />
          <Button
            className="!w-auto !p-2"
            color="neutral"
            variant="ghost"
            size="fixed"
            tooltip="Download"
            icon={<DownloadIcon />}
            onClick={() => window.open(link.url, "_blank", "noopener,noreferrer")}
          />
        </div>
      )}
    </article>
  );
}

export function AssetPreviewModal({
  target,
  onClose,
}: {
  target: AssetPreviewTarget | null;
  onClose: () => void;
}): ReactElement {
  const [preview, setPreview] = useState("");
  const [isTruncated, setIsTruncated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);

  useEffect(() => {
    if (!target) {
      setPreview("");
      setIsTruncated(false);
      setPreviewFailed(false);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setPreview("");
    setIsLoading(true);
    setPreviewFailed(false);
    void (async () => {
      try {
        const text = await readAssetText(target.link.url, target.kind);
        if (cancelled) {
          return;
        }
        setIsTruncated(text.length > PREVIEW_CHAR_LIMIT);
        setPreview(text.slice(0, PREVIEW_CHAR_LIMIT));
      } catch {
        if (!cancelled) {
          setPreviewFailed(true);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [target]);

  return (
    <Modal
      isModalOpen={Boolean(target)}
      toggleModal={onClose}
      title={target ? `${target.title} — ${target.link.fileName}` : ""}
      compact
    >
      {isLoading && <p className="text-sm text-gray-500">Loading preview…</p>}
      {previewFailed && (
        <p className="text-sm text-gray-500">
          Preview could not be loaded from storage. Download the file to inspect
          it.
        </p>
      )}
      {!isLoading && !previewFailed && preview && (
        <>
          {isTruncated && (
            <p className="mb-2 text-xs text-gray-500">
              Showing the first {PREVIEW_CHAR_LIMIT.toLocaleString()} characters.
              Download the file for the rest.
            </p>
          )}
          <pre className="max-h-[min(60vh,32rem)] overflow-auto rounded-md bg-gray-50 p-3 text-xs leading-relaxed text-gray-800">
            {preview}
          </pre>
        </>
      )}
      {target && (
        <div className="mt-4 flex justify-end">
          <Button
            className="!w-auto"
            text="Download"
            color="secondary"
            variant="outlined"
            size="fixed"
            onClick={() =>
              window.open(target.link.url, "_blank", "noopener,noreferrer")
            }
          />
        </div>
      )}
    </Modal>
  );
}

/**
 * Storage may answer without CORS. A failed fetch is a preview failure, not a
 * missing file — the signed URL still downloads in a new tab.
 */
async function readAssetText(url: string, kind: AssetKind): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Storage answered ${response.status}`);
  }

  const text = await response.text();
  if (kind !== "json") {
    return text;
  }

  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

function DownloadIcon(): ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden="true"
    >
      <path d="M12 3v11" />
      <path d="m7.5 10.5 4.5 4.5 4.5-4.5" />
      <path d="M4 21h16" />
    </svg>
  );
}
